// SUB-OFFICE 7 — FORM 5-D, THE DISCREPANCY WINDOW. Documents slide under the
// glass; the placard prints the day's disqualifying rules; a grease pencil
// circles the forgery and the stamp clears or rejects. Mid-shift the placard
// reprints with a new rule (the amendment). Docs + planted violations
// regenerate from the seed via docs5D(); the client records ONLY raw
// circles/loupe/stamp events — the server recomputes every forgery and band.

import { docs5D, rulesForDoc5D, RULES_5D, type Field5D, type Form5DSpec } from '../../shared/physspec';
import { holdSlamButton, printLayer } from '../ui/chrome';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';

// Display atlases (labels only; correctness is server-side against docs5D).
const SEAL_NAMES = ['THE BROKEN RING', 'THE INVERTED ARCH', 'THE DOUBLED EYE', 'THE HOLLOW STAR', 'THE LEANING GATE', 'THE SECOND MOON', 'THE PLAIN DISC', 'THE TWIN BAR', 'THE OPEN HAND', 'THE CLOSED GATE', 'THE FULL RING', 'THE UNMARKED'];
const DATE_NAMES = ['FIRST QUARTER', 'SECOND QUARTER', 'THIRD QUARTER', 'FOURTH QUARTER', 'FIFTH QUARTER', 'SIXTH QUARTER', 'INTERCALARY', 'UNDATED'];
const DESIG_NAMES = ['SERIES -A', 'SERIES -B', 'SERIES -C', 'SERIES -D', 'SERIES -E', 'SERIES -F', 'SERIES -G', 'SERIES -H'];
const FEE_NAMES = ['DENOMINATION 1', 'DENOMINATION 2', 'DENOMINATION 3', 'DENOMINATION 4', 'DENOMINATION 5', 'DENOMINATION 6', 'DENOMINATION 7', 'DENOMINATION 8'];
const SIG_NAMES = ['INK: GREY', 'INK: VIOLET', 'INK: RUST', 'INK: BLACK', 'INK: BLUE', 'INK: SEPIA'];
const WMK_NAMES = ['CHEVRON', 'LATTICE', 'RING', 'PLAIN', 'DOUBLE', 'NONE'];
const FIELD_ATLAS: Record<Field5D, string[]> = {
  seal: SEAL_NAMES, date: DATE_NAMES, designation: DESIG_NAMES, fee: FEE_NAMES, signature: SIG_NAMES, watermark: WMK_NAMES,
};
const FIELD_ORDER: Field5D[] = ['seal', 'date', 'designation', 'fee', 'signature', 'watermark'];

export function runDiscrepancy(
  deps: BenchDeps,
  taskId: string,
  spec: Form5DSpec
): Promise<BehaviorLog | null> {
  return new Promise((resolve) => {
    const rec = new PhysLogRecorder('FORM5D', taskId);
    const docs = docs5D(spec);
    const layer = printLayer();

    const card = document.createElement('div');
    card.className = 'paper-modal wide';
    card.innerHTML =
      `<div class="paper-head letterhead"><span>FORM 5-D — THE DISCREPANCY WINDOW</span>` +
      `<span class="tab-num" id="dw-count">1 / ${spec.doc_count}</span></div>`;
    const bodyWrap = document.createElement('div');
    bodyWrap.style.cssText = 'display:flex;gap:0;align-items:stretch';
    card.appendChild(bodyWrap);

    // ---- the placard (day's disqualifying rules) ---------------------------
    const placard = document.createElement('div');
    placard.style.cssText = 'flex:0 0 42%;border-right:1px solid var(--carbon);padding:12px 14px;background:var(--paper);font-size:11px;line-height:1.5';
    bodyWrap.appendChild(placard);

    // ---- the document under glass ------------------------------------------
    const glass = document.createElement('div');
    glass.className = 'paper-body typewriter';
    glass.style.cssText = 'flex:1;padding:12px 16px';
    bodyWrap.appendChild(glass);

    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    card.appendChild(foot);
    layer.appendChild(card);

    const countEl = card.querySelector('#dw-count') as HTMLElement;

    let placardShownAt = rec.now();
    const renderPlacard = (docIdx: number): void => {
      const ids = rulesForDoc5D(spec, docIdx);
      placard.innerHTML = '<div class="field-label">DISQUALIFYING RULES — THIS SHIFT</div>';
      ids.forEach((id) => {
        const r = document.createElement('div');
        r.style.cssText = 'margin:7px 0;padding-left:8px;border-left:2px solid var(--carbon)';
        const isAmend = spec.amendment_rule_id === id && spec.amendment_at !== null && docIdx >= spec.amendment_at;
        r.innerHTML = (isAmend ? '<span style="color:var(--stamp-red)">AMENDED · </span>' : '') + RULES_5D[id].text;
        placard.appendChild(r);
      });
      placardShownAt = rec.now();
    };

    let idx = 0;
    let over = false;
    let amendmentEmitted = false;
    const abandonBtn = document.createElement('button');
    abandonBtn.type = 'button';
    abandonBtn.className = 'slam-btn secondary';
    abandonBtn.textContent = 'ABANDON THE WINDOW';
    foot.appendChild(abandonBtn);

    const finishOk = (): void => {
      if (over) return;
      over = true;
      cleanup();
      card.remove();
      const built = rec.build(false);
      if (built.valid) resolve(built.log);
      else {
        deps.bus.emit('toast', `The inspection log did not assemble (${built.reason}).`);
        resolve(null);
      }
    };
    const abandon = (): void => {
      if (over) return;
      over = true;
      rec.ev('shift_abandoned', { doc: idx });
      cleanup();
      card.remove();
      resolve(null);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') abandon();
    };
    const cleanup = (): void => window.removeEventListener('keydown', onKey);
    window.addEventListener('keydown', onKey);
    abandonBtn.addEventListener('click', abandon);

    const showDoc = (): void => {
      if (over) return;
      if (idx >= spec.doc_count) {
        deps.bus.emit('chyron', 'THE WINDOW CLOSES. THE FORGERIES ARE COUNTED BY THE BACK OFFICE.');
        finishOk();
        return;
      }
      countEl.textContent = `${idx + 1} / ${spec.doc_count}`;
      // mid-shift amendment
      if (spec.amendment_at !== null && idx === spec.amendment_at && !amendmentEmitted) {
        amendmentEmitted = true;
        rec.ev('amendment_shown', { rule_id: spec.amendment_rule_id });
        deps.bus.emit('audio:buzz');
        deps.bus.emit('chyron', 'A NEW RULE IS POSTED MID-SHIFT. THE PLACARD REPRINTS. YOU ARE EXPECTED TO HAVE KNOWN.');
      }
      renderPlacard(idx);
      rec.ev('placard_dwell', { ms: Math.max(1, rec.now() - placardShownAt) });
      rec.ev('doc_shown', { doc: idx });

      const doc = docs[idx];
      const circled = new Set<Field5D>();
      glass.innerHTML = '';
      const head = document.createElement('div');
      head.className = 'field-label';
      head.textContent = `DOCUMENT ${idx + 1}${idx === spec.decoy_idx ? '' : ''}`;
      glass.appendChild(head);

      FIELD_ORDER.forEach((f) => {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:4px 0;border-bottom:1px dashed rgba(28,26,23,.2)';
        const key = document.createElement('span');
        key.style.cssText = 'flex:0 0 82px;font-size:10px;letter-spacing:.1em;opacity:.7;text-transform:uppercase';
        key.textContent = f;
        const val = document.createElement('span');
        val.style.cssText = 'flex:1;font-size:13px';
        val.textContent = FIELD_ATLAS[f][doc.fields[f]] ?? `VALUE ${doc.fields[f]}`;
        const pencil = document.createElement('button');
        pencil.type = 'button';
        pencil.className = 'slam-btn secondary';
        pencil.style.cssText = 'padding:5px 9px;font-size:9px';
        pencil.textContent = 'CIRCLE';
        pencil.addEventListener('click', () => {
          if (circled.has(f)) {
            circled.delete(f);
            val.style.outline = '';
            val.style.color = '';
            pencil.textContent = 'CIRCLE';
            rec.ev('circle_erased', { doc: idx, field: f });
          } else {
            circled.add(f);
            val.style.outline = '2px solid var(--stamp-red)';
            val.style.color = 'var(--stamp-red)';
            pencil.textContent = 'ERASE';
            rec.ev('field_circled', { doc: idx, field: f });
            deps.bus.emit('audio:tick');
          }
        });
        // loupe: press-and-hold to inspect (records inspection duration)
        const loupe = document.createElement('button');
        loupe.type = 'button';
        loupe.className = 'slam-btn secondary';
        loupe.style.cssText = 'padding:5px 9px;font-size:9px';
        loupe.textContent = 'LOUPE';
        let loupeAt = 0;
        loupe.addEventListener('pointerdown', () => { loupeAt = rec.now(); val.style.fontWeight = '700'; });
        const endLoupe = (): void => {
          if (!loupeAt) return;
          const ms = Math.max(1, rec.now() - loupeAt);
          loupeAt = 0;
          val.style.fontWeight = '';
          rec.ev('loupe', { doc: idx, field: f, ms });
        };
        loupe.addEventListener('pointerup', endLoupe);
        loupe.addEventListener('pointerleave', endLoupe);
        row.append(key, val, pencil, loupe);
        glass.appendChild(row);
      });

      // ---- the two stamp pads ----------------------------------------------
      const pads = document.createElement('div');
      pads.style.cssText = 'display:flex;gap:14px;justify-content:center;margin-top:14px';
      glass.appendChild(pads);
      let stamped = false;
      const stamp = (verdict: 'CLEAR' | 'REJECT') => (r: { heldMs: number }): void => {
        if (stamped) return;
        stamped = true;
        rec.ev('stamp', { doc: idx, verdict, holdMs: Math.round(r.heldMs) });
        deps.bus.emit('audio:thunk');
        idx++;
        window.setTimeout(showDoc, 220);
      };
      pads.appendChild(holdSlamButton('CLEAR', stamp('CLEAR')));
      pads.appendChild(holdSlamButton('REJECT', stamp('REJECT'), { className: 'red' }));
    };

    deps.bus.emit('chyron', 'THE DISCREPANCY WINDOW OPENS. READ THE PLACARD. CIRCLE WHAT LIES. STAMP IT.');
    showDoc();
  });
}
